import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import subscriptionService from '../services/subscriptionService';
import SubscriptionWall from './Subscription/SubscriptionWall';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireSubscription?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireSubscription = true,
}) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null);
  const [showSubscriptionWall, setShowSubscriptionWall] = useState(false);

  useEffect(() => {
    checkAuth();
    if (requireSubscription) {
      checkSubscription();
    } else {
      setHasActiveSubscription(true); // Don't check if not required
    }
  }, [requireSubscription]);

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
  };

  const checkSubscription = async () => {
    try {
      const isActive = await subscriptionService.isSubscriptionActive();
      setHasActiveSubscription(isActive);

      // Show subscription wall if not active
      if (!isActive) {
        setShowSubscriptionWall(true);
      }
    } catch (err) {
      console.error('Failed to check subscription:', err);
      setHasActiveSubscription(false);
      setShowSubscriptionWall(true);
    }
  };

  // Loading state
  if (isAuthenticated === null || (requireSubscription && hasActiveSubscription === null)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check subscription (but don't block, just show wall)
  if (requireSubscription && !hasActiveSubscription) {
    return (
      <>
        {children}
        <SubscriptionWall
          isOpen={showSubscriptionWall}
          onClose={() => setShowSubscriptionWall(false)}
          message="Your subscription has expired. Please upgrade to continue using this feature."
          allowClose={false}
        />
      </>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
